import type { PlayerStats, SuspiciousPlayBand } from "@/lib/demo-parser";

export interface SuspiciousPlayBandMeta {
  band: SuspiciousPlayBand;
  label: string;
  rangeLabel: string;
  description: string;
  example: string;
  containerClass: string;
  softClass: string;
  accentClass: string;
  textClass: string;
}

export const suspiciousPlayBandGuide: SuspiciousPlayBandMeta[] = [
  {
    band: "low",
    label: "Low",
    rangeLabel: "0-24",
    description: "The parsed signals stayed inside ordinary match variance.",
    example: "Typical ADR, modest headshot rate, and no unusual smoke or wallbang kill patterns.",
    containerClass:
      "border-[rgba(79,163,255,0.24)] bg-[rgba(79,163,255,0.12)] text-[rgb(205,228,255)]",
    softClass: "bg-[rgba(79,163,255,0.08)] border-[rgba(79,163,255,0.18)]",
    accentClass: "bg-[rgb(79,163,255)]",
    textClass: "text-[rgb(189,224,255)]",
  },
  {
    band: "notable",
    label: "Notable",
    rangeLabel: "25-49",
    description: "Some signals stand out, but the overall pattern is still limited.",
    example: "One or two signals crossed the notable range, but sample size or supporting evidence stayed mixed.",
    containerClass:
      "border-[rgba(123,188,103,0.24)] bg-[rgba(123,188,103,0.12)] text-[rgb(218,242,205)]",
    softClass: "bg-[rgba(123,188,103,0.08)] border-[rgba(123,188,103,0.18)]",
    accentClass: "bg-[rgb(123,188,103)]",
    textClass: "text-[rgb(208,239,188)]",
  },
  {
    band: "elevated",
    label: "Elevated",
    rangeLabel: "50-74",
    description: "Several signals lined up strongly enough to merit manual review.",
    example: "High kill efficiency plus rapid aim snaps and unusually strong smoke or wallbang conversion.",
    containerClass:
      "border-[rgba(255,179,71,0.28)] bg-[rgba(255,179,71,0.12)] text-[rgb(255,224,176)]",
    softClass: "bg-[rgba(255,179,71,0.08)] border-[rgba(255,179,71,0.18)]",
    accentClass: "bg-[rgb(255,179,71)]",
    textClass: "text-[rgb(255,214,149)]",
  },
  {
    band: "severe",
    label: "Severe",
    rangeLabel: "75-100",
    description: "Multiple strong signals stacked together in the same parsed match.",
    example: "A large cluster of high-impact signals persisted across a sufficiently strong sample in one match.",
    containerClass:
      "border-[rgba(255,107,107,0.28)] bg-[rgba(255,107,107,0.12)] text-[rgb(255,190,190)]",
    softClass: "bg-[rgba(255,107,107,0.08)] border-[rgba(255,107,107,0.18)]",
    accentClass: "bg-[rgb(255,107,107)]",
    textClass: "text-[rgb(255,142,142)]",
  },
];

export function resolveSuspiciousPlayBand(
  score?: number | null,
  band?: SuspiciousPlayBand | null
): SuspiciousPlayBand {
  if (band) {
    return band;
  }

  if (score == null) {
    return "low";
  }

  if (score >= 75) {
    return "severe";
  }
  if (score >= 50) {
    return "elevated";
  }
  if (score >= 25) {
    return "notable";
  }
  return "low";
}

export function getSuspiciousPlayBandMeta(
  score?: number | null,
  band?: SuspiciousPlayBand | null
) {
  const resolvedBand = resolveSuspiciousPlayBand(score, band);
  return (
    suspiciousPlayBandGuide.find((entry) => entry.band === resolvedBand) ??
    suspiciousPlayBandGuide[0]
  );
}

export function formatSuspiciousPlaySampleStrength(sampleStrength?: number | null) {
  if (sampleStrength == null) {
    return "Unknown";
  }

  const percentage = Math.round(sampleStrength * 100);
  if (percentage >= 75) {
    return `${percentage}% strong sample`;
  }
  if (percentage >= 45) {
    return `${percentage}% moderate sample`;
  }
  return `${percentage}% limited sample`;
}

export function pickSuspiciousPlayDefaultPlayer(
  players: PlayerStats[],
  preferredSteamId?: string
) {
  if (preferredSteamId) {
    const preferred = players.find((player) => player.steamId === preferredSteamId);
    if (preferred) {
      return preferred.steamId;
    }
  }

  return (
    players
      .slice()
      .sort(
        (left, right) =>
          (right.suspiciousPlayScore ?? 0) - (left.suspiciousPlayScore ?? 0) ||
          right.adr - left.adr
      )[0]?.steamId ?? null
  );
}
